/* eslint-disable no-underscore-dangle */
/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/no-string-refs */
/* eslint-disable @typescript-eslint/no-this-alias */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  ChangeEvent,
  ReactNode,
} from 'react';
import { useEffectOnce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import { useQueryClient } from '@tanstack/react-query';
import {
  useLocation,
  PageSwitcherTypes,
  useUrlBuilder,
  useHistory,
} from '@app/router';
import { useExperiment } from '@app/experiments';
import { EXPERIMENTS } from '@app/config';
import { Events, Categories, useGATrackEvent } from '@app/analytics';
import {
  EVENTS,
  hasImage,
  SCREENS,
  trackEvent,
  trackScreen,
} from '@app/segment';
import { Portal, useBreakpointValue } from '@stockx/ui';
import { ConditionalWrapper, SearchInput } from '@app/ui';
import { useCurrency } from '@app/currency';
import { getCustomer } from '@app/redux/customer/selectors';
import { getBanners } from '@app/redux/ui/selectors';
import useEdgeInfiniteQuery from '@app/data/edge/hooks/useEdgeInfiniteQuery';
import { navigateToBrowsePageWithFilters } from '../../utils';
import SearchResults from './SearchResultsLazy';
import GET_SEARCH_RESULTS from './queries/GetSearchResults';
import { SearchInputWrapper, SearchOverlayWrapper } from './SearchBox.styles';

interface SearchBoxSiteProps {
  resultsInline?: boolean;
  selectCallback?: (selectedProduct: object) => void;
}

/**
 * Global Site Search.
 *
 * This renders the results set in a position on the page
 * that allows for it to take over the whole page. Not attached
 * to the Input.
 */

const SearchOverlayPortal = (children: ReactNode) => (
  <Portal>
    <SearchOverlayWrapper>{children}</SearchOverlayWrapper>
  </Portal>
);

const FOUND_V3_ELEMENT_HEIGHT = 93;
const FOUND_DEFAULT_ELEMENT_HEIGHT = 132;
const getNumberOfItems = (
  ab_pirate_highlight_searchbar_desktop_v4_web: boolean,
) => {
  const navElems = document.getElementsByTagName(
    'nav',
  ) as HTMLCollectionOf<HTMLElement>;

  if (!navElems.length) {
    return 10;
  }

  const total = window.innerHeight - navElems[0].offsetHeight;
  const num =
    total /
    (ab_pirate_highlight_searchbar_desktop_v4_web
      ? FOUND_V3_ELEMENT_HEIGHT
      : FOUND_DEFAULT_ELEMENT_HEIGHT);

  return Number.isNaN(total) ? 10 : Math.ceil(num / 10) * 10;
};

const SearchBoxSite = ({
  resultsInline = false,
  selectCallback,
}: SearchBoxSiteProps) => {
  const [numberOfItems, setNumberOfItems] = useState<number>(10);
  const customer = useSelector(getCustomer);
  const banners = useSelector(getBanners);
  const { isMobile } = useBreakpointValue();
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { currency } = useCurrency();
  const { t } = useTranslation();
  const trackGA = useGATrackEvent();
  const buildUrl = useUrlBuilder();
  const queryClient = useQueryClient();
  const urlSearchTerm = new URLSearchParams(search).get('s');

  const ab_pirate_highlight_searchbar_desktop_v4_web =
    useExperiment(EXPERIMENTS.ab_pirate_highlight_searchbar_desktop_v4_web) ===
    'true';

  // Static ranking a/b test
  const isStaticRankingEnabled =
    useExperiment(EXPERIMENTS.ab_search_static_ranking_v3_web) === 'variant';

  const [displayInlineResults, setDisplayInlineResults] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (urlSearchTerm) {
      setSearchQuery(urlSearchTerm);
    } else if (searchQuery && !urlSearchTerm) {
      setSearchQuery('');
    }
  }, [search]);

  useEffect(() => {
    const itemsNo: number = getNumberOfItems(
      ab_pirate_highlight_searchbar_desktop_v4_web,
    );

    setNumberOfItems(itemsNo);
  }, []);

  const variables = {
    filtersVersion: 4,
    query: encodeURIComponent(searchQuery),
    sort: {
      id: 'featured',
      order: 'DESC',
    },
    staticRanking: { enabled: isStaticRankingEnabled },
  };

  const {
    data,
    isLoading,
    isSuccess,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
  } = useEdgeInfiniteQuery(GET_SEARCH_RESULTS, {
    variables,
    enabled: false,
    numberOfItems: numberOfItems > 10 ? numberOfItems : 10,
    getNextPageParam: (lastPage) => {
      // lastPage is the prev data returned from GET_SEARCH_RESULTS
      const pageInfo = lastPage?.browse?.results?.pageInfo;
      if (!pageInfo) {
        return null;
      }

      const totalPages = (pageInfo.total || 0) / (pageInfo.limit || 0);
      return pageInfo.page < totalPages ? (pageInfo.page || 0) + 1 : null;
    },
    formatContext: (queryFunctionContext) => {
      const nextIndex =
        queryFunctionContext.pageParam === 2
          ? numberOfItems / 10 + 1
          : queryFunctionContext.pageParam;

      const index = queryFunctionContext.pageParam ? nextIndex : 1;

      if (typeof index === 'number') {
        queryFunctionContext.queryKey[1].variables.page = {
          index,
          limit: index === 1 ? numberOfItems : 10,
        };
        queryFunctionContext.queryKey[1].variables.after = undefined;
      }

      if (typeof index === 'string') {
        queryFunctionContext.queryKey[1].variables.page = undefined;
        queryFunctionContext.queryKey[1].variables.after =
          queryFunctionContext.pageParam;
      }

      return queryFunctionContext;
    },
  });

  const algoliaIndex = data?.pages?.[0]?.browse?.results?.pageInfo?.queryIndex;

  const searchResultPages = useMemo(
    () => (searchQuery ? data?.pages : []),
    [data?.pages, searchQuery],
  );

  // Total # of products returned by search query
  const hitCount = useMemo(
    () => searchResultPages?.[0]?.browse?.results?.pageInfo?.total,
    [searchResultPages],
  );

  // # of product results currently visble to user
  const typeaheadResultsInDisplayCount = useMemo(
    () =>
      searchResultPages &&
      searchResultPages.reduce((prev, cur) => {
        const inDisplayCount =
          Object.prototype.hasOwnProperty.call(cur?.browse?.results, 'edges') &&
          prev + cur.browse.results.edges.length;

        return inDisplayCount || 0;
      }, 0),
    [searchResultPages],
  );

  // True if # total results (hitCount) is equal to # of results being displayed (typeaheadResultsInDisplayCount), false otherwise
  const isInfiniteSearchExhausted = useMemo(
    () => hitCount === typeaheadResultsInDisplayCount,
    [hitCount, typeaheadResultsInDisplayCount],
  );

  useEffectOnce(() => {
    if (isMobile) {
      const searchBox = document.querySelector('#site-search') as HTMLElement;
      if (searchBox) {
        searchBox.focus();
      }
    }
  });

  // eslint-disable-next-line arrow-body-style
  useEffect(() => {
    return history.listen(() => {
      queryClient.resetQueries(['get.searchItem'], { exact: true });
    });
  }, [history, queryClient]);

  const handleSelect = (listItem) => {
    if (!listItem) {
      return;
    }

    const selectedProduct = {
      id: listItem.id,
      urlKey: listItem.urlKey,
      title: listItem.primaryTitle,
      colorway: listItem.secondaryTitle,
      styleId: listItem.styleId,
      media: {
        thumbUrl: listItem.thumbnail_url,
      },
    };

    if (selectCallback) {
      selectCallback(selectedProduct);
    } else {
      const {
        product_category: vertical,
        objectID: productUUID,
        name: productName,
        uuid: skuUUID,
        lowest_ask: lowestAsk,
        thumbnail_url,
        position,
      } = listItem;

      trackEvent(EVENTS.PRODUCT_TILE, {
        screenName: SCREENS.QUERY_SUGGESTION,
        position,
        currency,
        vertical: listItem.productCategory,
        productUUID: listItem.id,
        productName: listItem.primaryTitle,
        hasImage: !!hasImage(thumbnail_url),
        lowestAsk,
        queryID: searchQuery,
        index: algoliaIndex,
        objectID: productUUID,
      });

      trackGA(
        Events.SearchSelectedItem,
        listItem?.productCategory,
        listItem?.urlKey,
      );

      trackEvent(EVENTS.SEARCH_RESULT_CLICKED, {
        screenName: SCREENS.SEARCH,
        key: searchQuery,
        vertical,
        productUUID,
        productName,
        skuUUID,
      });

      trackGA(
        Events.EnteredSearchQuery,
        Categories.Search,
        searchQuery,
        null,
        customer,
      );
      history.push(buildUrl(listItem?.urlKey), {
        as: PageSwitcherTypes.Product,
      });
    }

    reset();
  };

  const handleListItemKeyPress = (event) => {
    const elem = event.target;
    event.preventDefault();

    if (event.keyCode === 40 && elem.nextElementSibling) {
      elem.nextElementSibling.focus();
    } else if (event.keyCode === 38) {
      if (elem.previousElementSibling) {
        elem.previousElementSibling.focus();
      } else {
        const searchBox = document.querySelector('#site-search') as HTMLElement;
        searchBox.focus();
      }
    }
  };

  const filterOnVertical = (vertical) => {
    const url = buildUrl(
      `/search/${vertical}?s=${encodeURIComponent(searchQuery)}`,
    );

    trackGA(Events.SearchSelectedVertical, vertical, url);

    trackEvent(EVENTS.SEARCH_SUGGESTED_VERTICAL_CLICKED, {
      screenName: SCREENS.SEARCH,
      key: searchQuery,
      vertical,
    });

    history.push(url, {
      as: PageSwitcherTypes.Browse,
      from: pathname,
    });
    // After routing user, call reset with maintainSearchValue = true
    reset(true);
  };

  const loadMore = useCallback(() => {
    if (!isFetchingNextPage && searchResultPages) {
      fetchNextPage({
        cancelRefetch: false,
      });
    }
  }, [fetchNextPage, isFetchingNextPage, searchResultPages]);

  const reset = useCallback(
    (
      maintainSearchValue: boolean = false, // If true, searchQuery input state is not reset
      shouldNavigateToBrowsePage: boolean = false, // If true, user is routed to browse page
    ) => {
      if (!maintainSearchValue) {
        setSearchQuery('');
      }

      queryClient.resetQueries(['get.searchItem'], { exact: true });
      setDisplayInlineResults(false);
      if (shouldNavigateToBrowsePage) {
        navigateToBrowsePageWithFilters(pathname, search, history, buildUrl);
      }
    },
    [queryClient, pathname, search, setDisplayInlineResults, setSearchQuery],
  );

  const searchSubmit = () => {
    // If there is not value or no results do nothing
    // We don't want to redirect to an empty browse page
    if (!searchQuery || hitCount <= 0) {
      return;
    }

    history.push(buildUrl(`/search?s=${encodeURIComponent(searchQuery)}`), {
      as: PageSwitcherTypes.Browse,
      from: pathname,
    });
    // When submitting a search, call reset with maintainSearchValue = true
    reset(true);
    trackScreen(SCREENS.SEARCH, {});
    trackGA(
      Events.AlgoliaSearchIndex,
      Categories.ProductionPGFeaturedDescIndex,
      searchQuery,
    );
  };

  const handleSearchDownArrow = (event) => {
    if (event.keyCode === 40) {
      const firstItem = document.querySelector('.list-item') as HTMLElement;

      firstItem.focus();
      event.preventDefault();
    }

    if (event.keyCode === 13 && !resultsInline) {
      searchSubmit();
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const executeSearch = useCallback(
    debounce(() => {
      queryClient.invalidateQueries(['get.searchItem']);
      refetch();
    }, 500),
    [],
  );

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => {
      const searchValue = evt.target.value;
      setSearchQuery(searchValue);
      if (searchValue.length > 0) {
        setDisplayInlineResults(true);
        executeSearch();
        // If we are clearing the input on the search page, navigate to browse page with currently applied filters.
      } else if (searchQuery.length > 1 && pathname.includes('/search')) {
        reset(false, true);
      }
    },
    [executeSearch, searchQuery, setSearchQuery, pathname],
  );

  return (
    <div>
      <SearchInputWrapper data-component="SearchBoxSite">
        <SearchInput
          id="site-search"
          type="search"
          name="q"
          placeholder={t('Search for brand, color, etc.')}
          value={searchQuery}
          resultsInline={resultsInline}
          autoComplete="off"
          spellCheck={false}
          autoCorrect="off"
          onKeyDown={handleSearchDownArrow}
          autoCapitalize="off"
          handleChange={handleChange}
          data-testid="site-search"
          title={t('Search...')}
          aria-label={t('Search...')}
          onClose={() => reset(false, true)}
        />
      </SearchInputWrapper>
      {displayInlineResults && searchQuery && searchQuery.length > 0 ? (
        <ConditionalWrapper
          condition={!resultsInline}
          wrapper={SearchOverlayPortal}
        >
          <SearchResults
            banners={banners}
            categorySelection={filterOnVertical}
            handleListItemKeyPress={handleListItemKeyPress}
            handleSelect={handleSelect}
            isInfiniteSearchExhausted={isInfiniteSearchExhausted}
            isLoading={isLoading || isFetchingNextPage}
            isMobile={isMobile}
            isSuccess={isSuccess}
            loadMore={loadMore}
            resultsInline={resultsInline}
            searchQuery={searchQuery}
            searchResultPages={searchResultPages}
          />
        </ConditionalWrapper>
      ) : (
        <span />
      )}
    </div>
  );
};

export default SearchBoxSite;
